import { ICommonProcedure, Procedure } from '@dataTypes/appointment.type'
import { BundledProcedure } from '@dataTypes/bundledProcedure.type'
import { default as axios } from './axiosConfig'

import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Define the types for the response structure
export type AuthResponse = {
  statusCode: number
  message: string
  data:
    | {
        procedures: Procedure[]
      }
    | object
}

export interface ProcedureSearchRes {
  statusCode: number
  message: string
  data: {
    procedures: Procedure[]
  }
}

export interface CommonProcRes {
  statusCode: number
  message: string
  data: {
    common_procedures: ICommonProcedure[]
  }
}

export interface BundledProcRes {
  statusCode: number
  message: string
  data: {
    bundled_procedures: BundledProcedure[]
  }
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_PROCEDURE')
const headers = {
  'Content-Type': 'application/json',
}

const getProcedureReferenceValues = (): any => {
  return {
    user_id: JSON.parse(localStorage.getItem('user') || '{}')?.user_id || '',
    clinic_id: localStorage.getItem('clinic_id') || '',
    db_name: localStorage.getItem('db_name') || '',
  }
}

// Function to get access token
export const getAllProcedures = async (
  requiredProcedureSettings: boolean = false,
  requiredProcedureSettingsIsActive: boolean = false,
): Promise<AuthResponse> => {
  try {
    const { data, status } = await axios.get<AuthResponse>(
      `${BUSINESS_LAYER_API}/api/v1/procedurecodes`,
      {
        headers,
        params: {
          required_procedure_settings: requiredProcedureSettings,
          required_procedure_settings_is_active:
            requiredProcedureSettingsIsActive,
        },
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const searchProcedure = async (
  name: string,
  isActiveOnly: boolean = false,
): Promise<ProcedureSearchRes> => {
  try {
    const { data, status } = await axios.get<ProcedureSearchRes>(
      `${BUSINESS_LAYER_API}/api/v1/procedurecodes/search`,
      {
        headers,
        params: {
          name,
          ...(isActiveOnly && { is_active_only: true }),
        },
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch procedures')
    }

    return data
  } catch (error: any) {
    logger.error('Error fetching procedures:', error)
    throw toApiError(error, 'Error fetching procedures')
  }
}

export const getCommonProc = async (): Promise<CommonProcRes> => {
  try {
    const { data, status } = await axios.get<CommonProcRes>(
      `${BUSINESS_LAYER_API}/api/v1/commonprocedures`,
      {
        headers,
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch common procedures')
    }

    return data
  } catch (error: any) {
    logger.error('Error fetching procedures:', error)
    throw toApiError(error, 'Error fetching common procedures')
  }
}

export const addCommonProcedure = async (data: any): Promise<any> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/commonprocedures`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error adding common procedure:', error)
    throw toApiError(error, 'Error adding common procedure')
  }
}

export const updateCommonProcedure = async (
  common_proc_id: string,
  data: any,
): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/commonprocedures/${common_proc_id}`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error updating common procedure:', error)
    throw toApiError(error, 'Error updating common procedure')
  }
}

export const deleteCommonProcedure = async (
  common_proc_id: string,
): Promise<any> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/commonprocedures/${common_proc_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error deleting common procedure:', error)
    throw toApiError(error, 'Error deleting common procedure')
  }
}

export const getProcedureSettings = async (): Promise<AuthResponse> => {
  try {
    const { data, status } = await axios.get<AuthResponse>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings`,
      {
        headers,
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const addProcedureSettings = async (
  data: any,
): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error adding procedure settings:', error)
    throw toApiError(error, 'Error adding procedure settings')
  }
}

export const updateProcedureSettings = async (
  procedure_setting_id: string,
  data: any,
): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings/${procedure_setting_id}`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error updating procedure settings:', error)
    throw toApiError(error, 'Error updating procedure settings')
  }
}

export const importProcedureSettings = async (
  data: any,
): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings/csv/import`,
      { clinic_id, user_id, procedure_settings: data },
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error importing procedure settings:', error)
    throw toApiError(error, 'Error importing procedure settings')
  }
}

export const deleteProcedureSettings = async (
  procedure_setting_id: string,
): Promise<AuthResponse> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings/${procedure_setting_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error deleting procedure settings:', error)
    throw toApiError(error, 'Error deleting procedure settings')
  }
}

export const getBundledProcedures = async (): Promise<BundledProcRes> => {
  try {
    const { data, status } = await axios.get<BundledProcRes>(
      `${BUSINESS_LAYER_API}/api/v1/bundledprocedures`,
      {
        headers,
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch bundled procedures')
    }

    return data
  } catch (error: any) {
    logger.error('Error fetching bundled procedures:', error)
    throw toApiError(error, 'Error fetching bundled procedures')
  }
}

export const addBundledProcedure = async (data: any): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/bundledprocedures`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error adding bundled procedure:', error)
    throw toApiError(error, 'Error adding bundled procedure')
  }
}

export const updateBundledProcedure = async (
  bundled_procedure_id: string,
  data: any,
): Promise<AuthResponse> => {
  try {
    const { clinic_id, user_id } = getProcedureReferenceValues()
    data.clinic_id = clinic_id
    data.user_id = user_id
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/bundledprocedures/${bundled_procedure_id}`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error updating bundled procedure:', error)
    throw toApiError(error, 'Error updating bundled procedure')
  }
}

export const deleteBundledProcedure = async (
  bundled_procedure_id: string,
): Promise<AuthResponse> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/bundledprocedures/${bundled_procedure_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error deleting bundled procedure:', error)
    throw toApiError(error, 'Error deleting bundled procedure')
  }
}

export const checkBundledCodeIfExists = async (
  bundledCode: string,
  excludeBundledId?: string,
): Promise<any> => {
  try {
    const { clinic_id } = getProcedureReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      ...(excludeBundledId && {
        exclude_bundled_id: excludeBundledId,
      }),
    }

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/bundledprocedures/bundled-code-exists/${bundledCode}`,
      payload,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkBundledCodeIfExists:', e)
    throw toApiError(e, 'Error checking bundled code')
  }
}

export const checkProcedureCodeIfExists = async (
  procedureCode: string,
  excludeProcedureSettingsId?: string,
): Promise<any> => {
  try {
    const { clinic_id } = getProcedureReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      ...(excludeProcedureSettingsId && {
        exclude_procedure_settings_id: excludeProcedureSettingsId,
      }),
    }

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings/procedure-code-exists/${procedureCode}`,
      payload,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkProcedureCodeIfExists:', e)
    throw toApiError(e, 'Error checking procedure code')
  }
}

export const updateProcedureStatus = async (
  procedureSettingId: string,
  isActive: boolean,
): Promise<any> => {
  try {
    const { clinic_id } = getProcedureReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      is_active: isActive,
    }
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/proceduresettings/${procedureSettingId}/status`,
      payload,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateProcedureStatus:', e)
    throw toApiError(e, 'Error updating procedure status')
  }
}
